/**
 * Relative date presets and resolver for report filters.
 *
 * Converts preset keys like "past_30d" or "month_to_date" into concrete
 * { gte, lte } date strings (YYYY-MM-DD) at query time, keeping the
 * stored filter value dynamic so it auto-shifts on each load.
 */

export type RelativeDatePreset =
  // Rolling ranges
  | 'past_7d'
  | 'past_14d'
  | 'past_30d'
  | 'past_60d'
  | 'past_90d'
  | 'past_6mo'
  | 'past_1y'
  // Calendar time
  | 'today'
  | 'yesterday'
  | 'week_to_date'
  | 'month_to_date'
  | 'quarter_to_date'
  | 'year_to_date'
  | 'previous_week'
  | 'previous_month'
  | 'previous_quarter'
  | 'previous_year';

export interface RelativeDateOption {
  key: RelativeDatePreset;
  label: string;
  group: 'rolling' | 'calendar';
}

export const RELATIVE_DATE_OPTIONS: RelativeDateOption[] = [
  // Rolling ranges
  { key: 'past_7d', label: 'Past 7 Days', group: 'rolling' },
  { key: 'past_14d', label: 'Past 14 Days', group: 'rolling' },
  { key: 'past_30d', label: 'Past 30 Days', group: 'rolling' },
  { key: 'past_60d', label: 'Past 60 Days', group: 'rolling' },
  { key: 'past_90d', label: 'Past 90 Days', group: 'rolling' },
  { key: 'past_6mo', label: 'Past 6 Months', group: 'rolling' },
  { key: 'past_1y', label: 'Past 1 Year', group: 'rolling' },
  // Calendar time
  { key: 'today', label: 'Today', group: 'calendar' },
  { key: 'yesterday', label: 'Yesterday', group: 'calendar' },
  { key: 'week_to_date', label: 'Week to Date', group: 'calendar' },
  { key: 'month_to_date', label: 'Month to Date', group: 'calendar' },
  { key: 'quarter_to_date', label: 'Quarter to Date', group: 'calendar' },
  { key: 'year_to_date', label: 'Year to Date', group: 'calendar' },
  { key: 'previous_week', label: 'Previous Week', group: 'calendar' },
  { key: 'previous_month', label: 'Previous Month', group: 'calendar' },
  { key: 'previous_quarter', label: 'Previous Quarter', group: 'calendar' },
  { key: 'previous_year', label: 'Previous Year', group: 'calendar' },
];

/** Select items grouped for the dropdown */
export function getRelativeDateItems(): Array<{ key: string; label: string; group?: string }> {
  return RELATIVE_DATE_OPTIONS.map((o) => ({
    key: o.key,
    label: o.label,
    group: o.group === 'rolling' ? 'Rolling Range' : 'Calendar Time',
  }));
}

/** Look up the display label for a preset key */
export function getRelativeDateLabel(preset: string): string {
  return RELATIVE_DATE_OPTIONS.find((o) => o.key === preset)?.label || preset;
}

function formatDate(d: Date): string {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function addDays(d: Date, days: number): Date {
  const result = new Date(d);
  result.setDate(result.getDate() + days);
  return result;
}

function addMonths(d: Date, months: number): Date {
  const result = new Date(d);
  result.setMonth(result.getMonth() + months);
  return result;
}

function startOfWeek(d: Date): Date {
  const result = new Date(d);
  const day = result.getDay(); // 0 = Sunday
  result.setDate(result.getDate() - day);
  return result;
}

function startOfQuarter(d: Date): Date {
  const result = new Date(d);
  const month = result.getMonth();
  result.setMonth(month - (month % 3), 1);
  return result;
}

/**
 * Resolve a relative date preset into concrete { gte, lte } date strings.
 * Both boundaries are inclusive YYYY-MM-DD values.
 */
export function resolveRelativeDate(
  preset: string,
  now?: Date
): { gte: string; lte: string } | undefined {
  const today = now || new Date();
  const todayStr = formatDate(today);

  switch (preset) {
    // --- Rolling ranges ---
    case 'past_7d':
      return { gte: formatDate(addDays(today, -6)), lte: todayStr };
    case 'past_14d':
      return { gte: formatDate(addDays(today, -13)), lte: todayStr };
    case 'past_30d':
      return { gte: formatDate(addDays(today, -29)), lte: todayStr };
    case 'past_60d':
      return { gte: formatDate(addDays(today, -59)), lte: todayStr };
    case 'past_90d':
      return { gte: formatDate(addDays(today, -89)), lte: todayStr };
    case 'past_6mo':
      return { gte: formatDate(addMonths(today, -6)), lte: todayStr };
    case 'past_1y':
      return { gte: formatDate(addMonths(today, -12)), lte: todayStr };

    // --- Calendar time ---
    case 'today':
      return { gte: todayStr, lte: todayStr };
    case 'yesterday': {
      const y = formatDate(addDays(today, -1));
      return { gte: y, lte: y };
    }
    case 'week_to_date':
      return { gte: formatDate(startOfWeek(today)), lte: todayStr };
    case 'month_to_date': {
      const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
      return { gte: formatDate(monthStart), lte: todayStr };
    }
    case 'quarter_to_date':
      return { gte: formatDate(startOfQuarter(today)), lte: todayStr };
    case 'year_to_date': {
      const yearStart = new Date(today.getFullYear(), 0, 1);
      return { gte: formatDate(yearStart), lte: todayStr };
    }
    case 'previous_week': {
      const thisWeekStart = startOfWeek(today);
      const prevWeekStart = addDays(thisWeekStart, -7);
      const prevWeekEnd = addDays(thisWeekStart, -1);
      return { gte: formatDate(prevWeekStart), lte: formatDate(prevWeekEnd) };
    }
    case 'previous_month': {
      const prevMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const prevMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
      return { gte: formatDate(prevMonthStart), lte: formatDate(prevMonthEnd) };
    }
    case 'previous_quarter': {
      const qStart = startOfQuarter(today);
      const prevQStart = addMonths(qStart, -3);
      const prevQEnd = addDays(qStart, -1);
      return { gte: formatDate(prevQStart), lte: formatDate(prevQEnd) };
    }
    case 'previous_year': {
      const prevYearStart = new Date(today.getFullYear() - 1, 0, 1);
      const prevYearEnd = new Date(today.getFullYear() - 1, 11, 31);
      return { gte: formatDate(prevYearStart), lte: formatDate(prevYearEnd) };
    }
    default:
      return undefined;
  }
}

/**
 * Expand relative_date filter values in a filter map into concrete gte/lte pairs.
 * Returns a new map with relative_date keys replaced by their resolved range.
 * Non-relative keys are passed through unchanged.
 */
export function resolveRelativeDateFilters(
  filterValues: Record<string, unknown>
): Record<string, unknown> {
  const result: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(filterValues)) {
    if (key.endsWith('__relative_date') && typeof value === 'string') {
      const baseKey = key.replace(/__relative_date$/, '');
      const resolved = resolveRelativeDate(value);
      if (resolved) {
        result[`${baseKey}__gte`] = resolved.gte;
        result[`${baseKey}__lte`] = resolved.lte;
      }
    } else {
      result[key] = value;
    }
  }

  return result;
}
